import useSWR, { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'

import { ky } from './client'

export { useSWRConfig, useSWRMutation }

/**
 * OpenAPI paths as "auth/self" (without leading slash)
 * @typedef {{
 *   [K in keyof import('../../api/schema').paths]:
 *     K extends `/${infer P}` ? P : never
 * }[keyof import('../../api/schema').paths]} SchemaPathWithoutSlash
 */

/**
 * @template {SchemaPathWithoutSlash} T
 * @param {T} url
 * @param {boolean} [allow401=false]
 * @param {boolean} [suspense=true]
 */
export function useAPI(url, allow401 = false, suspense = true) {
  /** @type {import('swr').SWRResponse<import('../../api/schema').paths[`/${T}`]['get']['responses'][200]['content']['application/json']>} */
  const {
    data, //: data for the given key resolved by fetcher (or undefined if not loaded)
    error, //: error thrown by fetcher (or undefined)
    isValidating, //: if there's a request or revalidation loading
    mutate //(data?, shouldRevalidate?): function to mutate the cached data (details)
  } = useSWR(
    url,
    (url) =>
      ky
        .get(url)
        .then((a) => a.json())
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            if (allow401 && window.location.href.includes('/login')) return null
            console.warn('Unauthorized')
            return null
          }

          console.error('API Error', error)

          Sentry.withScope((scope) => {
            scope.setTag('api_endpoint', url)
            scope.setTag('error_name', error?.name ?? 'UnknownError')

            scope.setContext('api_request', {
              endpoint: url,
              pathname: window.location.pathname,
              hasResponse: !!error?.response,
              status: error?.response?.status,
              errorName: error?.name,
              errorMessage: error?.message,
              online: navigator.onLine
            })

            Sentry.captureException(error)
          })
          try {
            error.response
              .json()
              .then(({ message }) => toast.warn('Something unexpected happened: ' + message))
              .catch(() => toast.warn('Something unexpected happened, please try again in a few minutes'))
          } catch (e) {
            console.error(e)
            // If have an error, and we're not on an error page, redirect to the down page.
            if (window.location.pathname.indexOf('error') === -1) {
              window.location = '/error/down'
            }
          }
          return null
        }),
    {
      suspense,
      revalidateOnMount: true
    }
  )

  return { data, error, loading: isValidating, mutate }
}
